/**
 * pages/Onboarding.tsx
 * 3-step welcome shown after first signup.
 * Step 1: Welcome + name confirm
 * Step 2: Create first folder
 * Step 3: Create first note (then redirect to /dashboard)
 * Awaits: store.createFolder + store.createNote (wired — works with mock and real)
 */

import React, { useState } from 'react';
import { Check, ArrowRight, FolderOpen, FileText, Search } from 'lucide-react';
import { useStore } from '../store';
import { GlobalSpin } from '../components/ui';


const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623',
  amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF', green:'#16A34A',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const FOLDER_SUGGESTIONS = [
  { name:'Work notes',  color:'#F5A623', icon:'💼' },
  { name:'Study',       color:'#16A34A', icon:'📚' },
  { name:'Personal',    color:'#7C3AED', icon:'🌱' },
  { name:'Journal',     color:'#DB2777', icon:'📓' },
  { name:'Projects',    color:'#2563EB', icon:'🚀' },
  { name:'Formulas',    color:'#C4811A', icon:'🔢' },
];

const Onboarding: React.FC = () => {
  const createFolder  = useStore(s => s.createFolder);
  const createNote    = useStore(s => s.createNote);
  const setOpenNote   = useStore(s => s.setOpenNote);

  const [step, setStep]           = useState(1);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState(FOLDER_SUGGESTIONS[0].color);
  const [selectedSuggestion, setSelectedSuggestion] = useState<number | null>(0);
  const [noteTitle, setNoteTitle] = useState('');
  const [loading, setLoading]     = useState(false);
  const [createdFolderId, setCreatedFolderId] = useState<string | null>(null);

  const selectSuggestion = (i: number) => {
    setSelectedSuggestion(i);
    setFolderName(FOLDER_SUGGESTIONS[i].name);
    setFolderColor(FOLDER_SUGGESTIONS[i].color);
  };

  const handleFolderStep = async () => {
    if (!folderName.trim()) { setStep(3); return; } // skip if blank
    setLoading(true);
    await createFolder(folderName.trim(), folderColor, null);
    // Grab the newly created folder id
    const folders = useStore.getState().folders;
    const created = folders[folders.length - 1];
    setCreatedFolderId(created?.id || null);
    setLoading(false);
    setStep(3);
  };

  const handleFinish = async () => {
    setLoading(true);
    const note = await createNote(createdFolderId);
    if (noteTitle.trim()) {
      useStore.getState().updateNote(note.id, { title: noteTitle.trim() });
    }
    setOpenNote(note.id);
    setLoading(false);
    window.location.href = '/dashboard';
  };

  const skipToEnd = () => { window.location.href = '/dashboard'; };

  const steps = [
    { n:1, label:'Welcome' },
    { n:2, label:'First folder' },
    { n:3, label:'First note' },
  ];

  return (
    <>
      <GlobalSpin/>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};color:${C.ink};}
        input{-webkit-font-smoothing:antialiased;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        .fade-up{animation:fadeUp .4s ease both;}
      `}</style>

      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', background:`linear-gradient(160deg,#FFFDF8 60%,#FFF6E0 100%)` }}>

        {/* Step indicator */}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:44 }}>
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .2s',
                  background: step > s.n ? C.green : step === s.n ? C.amber300 : C.border,
                  color: step >= s.n ? C.white : C.muted,
                }}>
                  {step > s.n ? <Check size={13}/> : <span style={{ fontSize:12, fontWeight:700, fontFamily:font.head }}>{s.n}</span>}
                </div>
                <span style={{ fontSize:12, fontWeight: step === s.n ? 700 : 500, color: step === s.n ? C.ink : C.muted, fontFamily:font.body }}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && <div style={{ width:32, height:1.5, background: step > s.n ? C.green : C.border, transition:'background .3s' }}/>}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="fade-up" key={step} style={{ width:'100%', maxWidth:480, background:C.white, borderRadius:24, border:`1px solid ${C.border}`, padding:'36px 40px', boxShadow:'0 12px 48px rgba(0,0,0,0.06)' }}>

          {/* ── STEP 1: Welcome ─────────────────────────────────── */}
          {step === 1 && (
            <div style={{ textAlign:'center' }}>
              <div style={{ width:64, height:64, borderRadius:20, background:`linear-gradient(135deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px' }}>
                <Search size={28} color={C.white}/>
              </div>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:28, letterSpacing:'-0.04em', color:C.ink, marginBottom:10 }}>
                Welcome to Jötr{useStore.getState().user?.name ? `, ${useStore.getState().user!.name.split(' ')[0]}` : ''}! 🎉
              </h1>
              <p style={{ fontSize:15, color:C.muted, lineHeight:1.7, fontFamily:font.body, marginBottom:28 }}>
                You're 2 steps away from having a note app that actually lets you <strong>find anything</strong> you write. Let's set yours up.
              </p>
              {/* Feature preview pills */}
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:32, textAlign:'left' }}>
                {[
                  { icon:<Search size={14}/>, t:'Full-text search across all notes', c:C.amber300 },
                  { icon:<FolderOpen size={14}/>, t:'Folders and sub-folders for organisation', c:'#16A34A' },
                  { icon:<FileText size={14}/>, t:'Rich text editor with auto-save', c:'#7C3AED' },
                ].map((f,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10, background:C.surface, border:`1px solid ${C.border}` }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:f.c+'18', display:'flex', alignItems:'center', justifyContent:'center', color:f.c, flexShrink:0 }}>{f.icon}</div>
                    <span style={{ fontSize:13, color:C.inkLight, fontFamily:font.body, fontWeight:500 }}>{f.t}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setStep(2)} style={{ width:'100%', height:50, borderRadius:12, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:16, fontFamily:font.body, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                Let's set up Jötr <ArrowRight size={17}/>
              </button>
              <button onClick={skipToEnd} style={{ width:'100%', marginTop:10, padding:'10px', background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:13, fontFamily:font.body }}>
                Skip and go straight to my notes
              </button>
            </div>
          )}

          {/* ── STEP 2: First folder ─────────────────────────────── */}
          {step === 2 && (
            <div>
              <div style={{ width:48, height:48, borderRadius:15, background:folderColor+'22', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                <FolderOpen size={22} color={folderColor}/>
              </div>
              <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:24, letterSpacing:'-0.04em', color:C.ink, marginBottom:8 }}>Create your first folder</h2>
              <p style={{ fontSize:14, color:C.muted, lineHeight:1.6, marginBottom:24, fontFamily:font.body }}>
                Folders keep your notes organised. You can create as many as you need — and nest them inside each other.
              </p>

              {/* Suggestions */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
                {FOLDER_SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => selectSuggestion(i)} style={{
                    display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:20,
                    border:`1.5px solid ${selectedSuggestion === i ? s.color : C.border}`,
                    background: selectedSuggestion === i ? s.color+'18' : C.white,
                    cursor:'pointer', fontFamily:font.body, fontSize:12, fontWeight:600,
                    color: selectedSuggestion === i ? s.color : C.muted, transition:'all .12s',
                  }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:s.color }}/>
                    {s.name}
                  </button>
                ))}
              </div>

              {/* Custom name input */}
              <input
                value={folderName}
                onChange={e => { setFolderName(e.target.value); setSelectedSuggestion(null); }}
                placeholder="Or type your own folder name…"
                style={{ width:'100%', height:44, padding:'0 14px', borderRadius:10, border:`1.5px solid ${folderName ? C.amber300 : C.border}`, fontFamily:font.body, fontSize:14, color:C.ink, outline:'none', marginBottom:24, background: folderName ? C.amber100 : C.white, transition:'all .15s' }}
              />

              <div style={{ display:'flex', gap:10 }}>
                <button onClick={handleFolderStep} disabled={loading} style={{ flex:1, height:46, borderRadius:11, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, fontFamily:font.body }}>
                  {loading ? 'Creating…' : folderName.trim() ? 'Create folder →' : 'Skip for now →'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: First note ───────────────────────────────── */}
          {step === 3 && (
            <div>
              <div style={{ width:48, height:48, borderRadius:15, background:'#7C3AED18', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                <FileText size={22} color="#7C3AED"/>
              </div>
              <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:24, letterSpacing:'-0.04em', color:C.ink, marginBottom:8 }}>Name your first note</h2>
              <p style={{ fontSize:14, color:C.muted, lineHeight:1.6, marginBottom:24, fontFamily:font.body }}>
                What are you working on right now? Give it a title and Jötr will open it straight away.
              </p>
              <input
                autoFocus
                value={noteTitle}
                onChange={e => setNoteTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleFinish(); }}
                placeholder="e.g. Meeting notes, Study guide, Project ideas…"
                style={{ width:'100%', height:46, padding:'0 14px', borderRadius:10, border:`1.5px solid ${noteTitle ? C.amber300 : C.border}`, fontFamily:font.body, fontSize:15, color:C.ink, outline:'none', marginBottom:24, background: noteTitle ? C.amber100 : C.white, transition:'all .15s' }}
              />
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={handleFinish} disabled={loading} style={{ flex:1, height:46, borderRadius:11, border:'none', cursor:'pointer', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, fontFamily:font.body, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {loading ? 'Setting up…' : <>{noteTitle.trim() ? 'Open my note' : 'Start writing'} <ArrowRight size={16}/></>}
                </button>
              </div>
              <p style={{ textAlign:'center', fontSize:12, color:C.muted, marginTop:12, fontFamily:font.body }}>
                You can always create more notes and folders later.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Onboarding;
